const yup = require("yup");

/**
 * Generates a dynamic validation schema for custom fields.
 * @param {Array} customFieldSettings - Array of custom field settings.
 * @returns {yup.ObjectSchema} - A yup validation schema for custom fields.
 */
const generateCustomFieldsValidation = (
  customFieldSettings,
  statuses = ["ACTIVE"],
) => {
  const customFieldsSchema = {};

  // Filter fields based on the provided statuses
  // since only active statuses will be edited they are the only default validations to check on
  const filteredFields = customFieldSettings.filter((field) =>
    statuses.includes(field.status),
  );

  filteredFields.forEach((field) => {
    if (field.type === "CHECKBOX" && field.isRequired) {
      customFieldsSchema[field.id] = yup
        .array()
        .of(yup.string().oneOf(["true"], `${field.label} must be checked`))
        .min(1, `${field.label} must be checked`)
        .required(`${field.label} is required`);
    } else if (field.isRequired) {
      customFieldsSchema[field.id] = yup
        .array()
        .of(
          yup
            .string()
            .test(
              "is-non-empty",
              `${field.label} cannot be empty`,
              function (value) {
                // If the user added multiple text boxes, ALL must be filled
                if (value == null) return false;
                return String(value).trim() !== "";
              },
            ),
        )
        .test(
          "has-at-least-one-value",
          `${field.label} is required`,
          function (value) {
            // Ensure at least ONE non-empty value exists
            if (!value || value.length === 0) return false;
            return value.some(
              (item) => item != null && String(item).trim() !== "",
            );
          },
        )
        .required(`${field.label} is required`);
    } else {
      customFieldsSchema[field.id] = yup
        .array()
        .of(
          yup
            .string()
            .transform((value) => (value === "" ? null : value))
            .nullable(),
        )
        .nullable();
    }
  });

  return yup.object().shape(customFieldsSchema);
};

const customFieldValidations = yup.object().shape({
  id: yup.string(),
  label: yup.string().required("Label is required"),
  type: yup.string().required("Type is required"),
  parentTypes: yup
    .array()
    .of(yup.string())
    .min(1, "At least one option is required"),
  isRequired: yup.boolean(),
  options: yup
    .array()
    .of(yup.string())
    .when("type", {
      is: (type) => type === "LIST" || type === "RADIO",
      then: () =>
        yup
          .array()
          .of(yup.string().required("Option cannot be empty"))
          .min(2, "At least two options are required"),
    })
    .when("type", {
      is: "ALLOCATION",
      then: (schema) =>
        schema
          .of(yup.string().required("Allocation type is required"))
          .min(1, "Allocation type is required"),
    })
    .nullable(),
  allowMultipleEntries: yup.boolean(),
  status: yup.string(),
  order: yup
    .number()
    .positive("Order must be a positive number")
    .integer("Order must be an whoile number"),
});

export { customFieldValidations, generateCustomFieldsValidation };
